import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSubscription } from '@/hooks/useSubscription';
import dayjs from 'dayjs';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const DangerZone = () => {
  const { subscription, features, cancelSubscription, reactivateSubscription, loading } = useSubscription();
  const { t } = useTranslation('subscription');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const canCancel = (features.isActive || features.isTrialing) && !features.isCancelScheduled;
  const canReactivate = features.isCancelScheduled;

  if (!canCancel && !canReactivate) {
    return null;
  }

  console.log(subscription);
  return (
    <>
      <Card className='border-red-200 dark:border-red-800'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-red-600 dark:text-red-400'>
            <AlertTriangle className='size-5' />
            {t('dangerZone.title')}
          </CardTitle>
          <CardDescription>{t('dangerZone.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {canCancel && (
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-900 dark:text-white'>{t('dangerZone.cancelSubscription')}</p>
                <p className='text-sm text-slate-500 dark:text-stone-400'>
                  {t('dangerZone.cancelSubscriptionDescription')}
                </p>
              </div>
              <Button variant='destructive' onClick={() => setCancelDialogOpen(true)}>
                {t('dangerZone.cancelSubscriptionButton')}
              </Button>
            </div>
          )}

          {canReactivate && (
            <div className='space-y-3'>
              <div>
                <p className='text-sm font-medium text-slate-900 dark:text-white'>{t('dangerZone.cancellationScheduled')}</p>
                <p className='text-sm text-slate-500 dark:text-stone-400'>
                  {t('dangerZone.cancellationScheduledDescription', { date: subscription.currentPeriodEnd ? dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY') : t('dangerZone.cancellationScheduledFallback') })}
                </p>
              </div>
              <Button
                variant='outline'
                disabled={reactivating || loading}
                onClick={async () => {
                  setReactivating(true);
                  try {
                    await reactivateSubscription();
                    toast.success(t('dangerZone.reactivatedSuccess'));
                  } catch {
                    toast.error(t('dangerZone.reactivateFailed'));
                  } finally {
                    setReactivating(false);
                  }
                }}>
                {reactivating ? t('dangerZone.reactivating') : t('dangerZone.reactivate')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dangerZone.cancelDialogTitle')}</DialogTitle>
            <DialogDescription>
              {subscription.currentPeriodEnd
                ? t('dangerZone.cancelDialogDescriptionWithDate', { date: dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY') })
                : t('dangerZone.cancelDialogDescriptionNoDate')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
              {t('dangerZone.keepSubscription')}
            </Button>
            <Button
              variant='destructive'
              disabled={cancelling}
              onClick={async () => {
                setCancelling(true);
                try {
                  await cancelSubscription();
                  setCancelDialogOpen(false);
                } catch {
                  // error is surfaced via the hook's error state
                } finally {
                  setCancelling(false);
                }
              }}>
              {cancelling ? t('dangerZone.cancelling') : t('dangerZone.yesCancelSubscription')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
